import { useListAdminInterviews } from "@/api/generated/hooks/admin/admin";
import type { InterviewSummary } from "@/api/generated/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { unwrapBody } from "@/lib/api-response";
import { downloadBlob } from "@/lib/download";
import { REPORT_DOWNLOAD_ERROR_MESSAGES, downloadInterviewReport } from "@/lib/interview-report";
import { formatDateShort, formatRelativeTime, getDisplayName } from "@/lib/interview-ui";
import { useNavigate } from "@tanstack/react-router";
import { Download, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { roleLabels } from "./[candidateId]/_components/role-fit-card";
import { AiScoreCircle } from "./_components/ai-score-circle";
import { AiScoreInfoBanner } from "./_components/ai-score-info-banner";
import { CandidateFilterMenu } from "./_components/candidate-filter-menu";
import { CandidateRowAvatar } from "./_components/candidate-row-avatar";
import { type CandidateStatusTab, CandidateStatusTabs } from "./_components/candidate-status-tabs";
import { InterviewDisplayStatusBadge } from "./_components/interview-display-status-badge";
import { SortableTableHead } from "./_components/sortable-table-head";
import { buildCandidatesCsv, buildCandidatesCsvFileName } from "./_lib/candidate-csv";
import {
  APPLIED_DATE_FILTER_OPTIONS,
  type AppliedDateFilter,
  DEFAULT_SORT,
  SCORE_FILTER_OPTIONS,
  type ScoreFilter,
  type SortKey,
  type SortState,
  computeTabCounts,
  filterInterviews,
  findTopScoreInterviewId,
  nextSortState,
  sortInterviews,
} from "./_lib/candidate-list";

const PAGE_SIZE = 10;
const LIST_POLL_INTERVAL_MS = 30_000;
const CSV_MIME_TYPE = "text/csv;charset=utf-8";
const IN_PROGRESS_STATUSES: readonly InterviewSummary["status"][] = ["LIVE", "EVALUATING"];

export function CandidatesPage() {
  const navigate = useNavigate();
  const interviewsQuery = useListAdminInterviews({
    query: {
      select: (response) => unwrapBody(response).interviews,
      refetchInterval: (query) => {
        const data = query.state.data as { data?: { interviews?: InterviewSummary[] } } | undefined;
        const hasInProgress = data?.data?.interviews?.some((interview) =>
          IN_PROGRESS_STATUSES.includes(interview.status)
        );
        return hasInProgress ? LIST_POLL_INTERVAL_MS : false;
      },
    },
  });

  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<CandidateStatusTab>("all");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter>("all");
  const [appliedDateFilter, setAppliedDateFilter] = useState<AppliedDateFilter>("all");
  const [reportDownloadingId, setReportDownloadingId] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  const interviews = interviewsQuery.data ?? [];
  const counts = useMemo(
    () =>
      computeTabCounts(
        interviews,
        { search, score: scoreFilter, appliedDate: appliedDateFilter },
        new Date()
      ),
    [interviews, search, scoreFilter, appliedDateFilter]
  );

  const sorted = useMemo(() => {
    const evaluatedAt = new Date();
    return sortInterviews(
      filterInterviews(
        interviews,
        { search, tab, score: scoreFilter, appliedDate: appliedDateFilter },
        evaluatedAt
      ),
      sort,
      evaluatedAt
    );
  }, [interviews, search, tab, scoreFilter, appliedDateFilter, sort]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * PAGE_SIZE;
  const visible = sorted.slice(pageStart, pageStart + PAGE_SIZE);

  const topInterviewId = findTopScoreInterviewId(sorted);

  const handleTabChange = (next: CandidateStatusTab) => {
    setTab(next);
    setPage(0);
  };

  const handleSearchChange = (next: string) => {
    setSearch(next);
    setPage(0);
  };

  const handleSort = (key: SortKey) => {
    setSort((current) => nextSortState(current, key));
    setPage(0);
  };

  const handleScoreFilterChange = (next: ScoreFilter) => {
    setScoreFilter(next);
    setPage(0);
  };

  const handleAppliedDateFilterChange = (next: AppliedDateFilter) => {
    setAppliedDateFilter(next);
    setPage(0);
  };

  const handleCsvExport = () => {
    const exportedAt = new Date();
    downloadBlob(
      new Blob([buildCandidatesCsv(sorted, { now: exportedAt, origin: window.location.origin })], {
        type: CSV_MIME_TYPE,
      }),
      buildCandidatesCsvFileName(exportedAt)
    );
  };

  const handleReportDownload = async (interview: InterviewSummary) => {
    setReportDownloadingId(interview.interviewId);
    setReportError(null);
    const result = await downloadInterviewReport({
      candidateName: interview.ownerName,
      interviewId: interview.interviewId,
    });
    setReportDownloadingId(null);
    if (!result.ok) {
      setReportError(
        `${getDisplayName(interview.ownerName)}: ${REPORT_DOWNLOAD_ERROR_MESSAGES[result.reason]}`
      );
    }
  };

  return (
    <main className="flex-1 overflow-y-auto bg-slate-50">
      <div className="max-w-[1600px] mx-auto px-6 py-6 flex flex-col gap-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">候補者評価一覧</h1>
            <p className="text-sm text-slate-500 max-w-xl">
              応募日の新しい順に表示します。列見出しをクリックすると並び替えできます。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <div className="relative group w-full sm:w-64 lg:w-80">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-teal-600 transition-colors">
                <Search className="size-5" />
              </span>
              <Input
                type="text"
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="名前やキーワードで検索..."
                className="w-full h-10 pl-10 pr-4 rounded-lg border-slate-200 bg-white text-sm shadow-sm placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-teal-500/20 focus-visible:border-teal-500"
                aria-label="候補者検索"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={interviewsQuery.isLoading || total === 0}
              onClick={handleCsvExport}
              title="表示中の候補者一覧をCSVで出力"
              className="h-10 px-4 rounded-lg bg-white border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 hover:text-teal-700 hover:border-teal-200 shadow-sm"
            >
              <Download className="size-5" />
              レポート出力
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <CandidateStatusTabs value={tab} onChange={handleTabChange} counts={counts} />
            <div className="h-8 w-px bg-slate-200 mx-1" aria-hidden="true" />
            <CandidateFilterMenu
              label="スコア範囲"
              value={scoreFilter}
              options={SCORE_FILTER_OPTIONS}
              onChange={handleScoreFilterChange}
            />
            <CandidateFilterMenu
              label="応募日"
              value={appliedDateFilter}
              options={APPLIED_DATE_FILTER_OPTIONS}
              onChange={handleAppliedDateFilterChange}
            />
          </div>
        </div>

        {reportError && (
          <p role="alert" className="text-sm text-rose-600">
            {reportError}
          </p>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="border-collapse">
              <TableHeader>
                <TableRow className="bg-slate-50/50 border-b border-slate-200 hover:bg-slate-50/50">
                  <SortableTableHead
                    label="氏名"
                    sortKey="name"
                    sort={sort}
                    onSort={handleSort}
                    className="py-2 pl-6 pr-4 text-[10px] font-semibold text-slate-500 uppercase tracking-wider min-w-[220px] h-auto"
                  />
                  <SortableTableHead
                    label="ステータス"
                    sortKey="status"
                    sort={sort}
                    onSort={handleSort}
                    className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider h-auto"
                  />
                  <SortableTableHead
                    label="AIスコア"
                    sortKey="score"
                    sort={sort}
                    onSort={handleSort}
                    className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-center h-auto"
                  />
                  <SortableTableHead
                    label="応募日"
                    sortKey="createdAt"
                    sort={sort}
                    onSort={handleSort}
                    className="py-2 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider h-auto"
                  />
                  <TableHead className="py-2 px-3 pr-6 text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-right h-auto">
                    操作
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y divide-slate-100">
                {interviewsQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="px-6 py-8 text-center text-sm text-slate-500">
                      読み込み中...
                    </TableCell>
                  </TableRow>
                )}
                {interviewsQuery.isError && (
                  <TableRow>
                    <TableCell colSpan={5} className="px-6 py-8 text-center text-sm text-rose-600">
                      面談一覧の取得に失敗しました。
                    </TableCell>
                  </TableRow>
                )}
                {!interviewsQuery.isLoading && !interviewsQuery.isError && visible.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="px-6 py-12 text-center text-sm text-slate-400"
                    >
                      一致する候補者がいません。
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((interview) => {
                  const displayName = getDisplayName(interview.ownerName);
                  const rank = interview.interviewId === topInterviewId ? 1 : undefined;
                  return (
                    <TableRow
                      key={interview.interviewId}
                      className="group cursor-pointer transition-colors hover:bg-teal-50/40"
                      onClick={() =>
                        navigate({
                          to: "/candidates/$candidateId",
                          params: { candidateId: interview.interviewId },
                        })
                      }
                    >
                      <TableCell className="py-2 pl-6 pr-4">
                        <div className="flex items-center gap-3">
                          <CandidateRowAvatar name={displayName} rank={rank} />
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-900 group-hover:text-teal-700 transition-colors truncate">
                              {displayName}
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="min-w-0 text-[11px] text-slate-500 truncate">
                                {interview.ownerEmail ?? "メール未登録"}
                              </p>
                              {interview.primaryRole && (
                                <Badge variant="outline">
                                  {roleLabels[interview.primaryRole] ?? interview.primaryRole}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-3">
                        <InterviewDisplayStatusBadge
                          status={interview.status}
                          createdAt={interview.createdAt}
                        />
                      </TableCell>
                      <TableCell className="py-2 px-3 text-center">
                        <AiScoreCircle score={interview.overallScore} status={interview.status} />
                      </TableCell>
                      <TableCell className="py-2 px-3">
                        <div className="text-xs font-medium text-slate-700">
                          {formatDateShort(interview.createdAt)}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {formatRelativeTime(interview.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-3 pr-6 text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={
                            interview.overallScore === null ||
                            reportDownloadingId === interview.interviewId
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleReportDownload(interview);
                          }}
                          aria-label={`${displayName}のレポートをダウンロード`}
                          className="text-teal-600 hover:text-teal-700 font-semibold text-xs h-auto py-1.5 px-2 rounded-lg hover:bg-teal-50 disabled:text-slate-300"
                        >
                          <Download className="size-4" />
                          ダウンロード
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              全<span className="font-bold text-slate-900 mx-1">{total}</span>件中
              <span className="font-bold text-slate-900 mx-1">
                {total === 0 ? 0 : pageStart + 1}-{pageStart + visible.length}
              </span>
              件を表示
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={safePage === 0}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
                className="h-9 px-3 rounded-lg border-slate-200 text-slate-500 hover:text-teal-700 hover:border-teal-200 hover:bg-slate-50"
              >
                前へ
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                className="h-9 px-3 rounded-lg border-slate-200 text-slate-500 hover:text-teal-700 hover:border-teal-200 hover:bg-slate-50"
              >
                次へ
              </Button>
            </div>
          </div>
        </div>

        <AiScoreInfoBanner />
      </div>
    </main>
  );
}
